import { FieldArray, FormikProvider, useFormik } from "formik";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TAB_NAMES } from "src/apps/common/menu-navigation/menuNavigation";
import TabHeader from "src/apps/common/tab-header/TabHeader";
import Button from "src/components/buttons/Button";
import DatePicker from "src/components/calendar/DatePicker";
import Dropdown from "src/components/dropdowns/Dropdown";
import Input, { ErrorMessage } from "src/components/inputs/Input";
import SearchList from "src/components/search-list/SearchList";
import HorizontalSeparator from "src/components/seperators/HorizontalSeperator";
import TimePicker from "src/components/time/TimePicker";
import { generateRandomId } from "src/helpers/helpers";
import useDebounce from "src/hooks/useDebounce";
import useNotification from "src/hooks/useNotification";
import useSetActiveTab from "src/hooks/useSetActiveTab";
import { AddIcon } from "src/icons/AddIcon";
import CallIcon from "src/icons/CallIcon";
import { CloseIcon } from "src/icons/CloseIcon";
import { APPOINTMENT_TYPES, GENDERS } from "src/utils/constants";
import { array, number, object, string } from "yup";
import { useGetClients } from "../../client-management/api-clients";
import { useGetCourses } from "../../courses/api-clients";
import { useGetPackages } from "../../packages/api-clients";
import { useGetServices } from "../../services/api-clients";
import { useGetStaff } from "../../staff-management/api-clients";
import {
  useCreateAppointment,
  useGetAppointmentData,
  useUpdateAppointment,
} from "../api-clients";

const SERVICE_SCHEMA = object().shape({
  serviceId: number().required("Required"),
  staffId: number().required("Required"),
  startTime: string().required("Required"),
  endTime: string().required("Required"),
  quantity: number().required("Required"),
  price: number().required("Required"),
});

const initialServiceValues = {
  id: generateRandomId(),
  type: 1,
  serviceId: null,
  staffId: null,
  startTime: null,
  endTime: null,
  quantity: null,
  price: null,
};

const FORM_FIELDS = {
  NAME: "name",
  SERVICES: "services",
  CLIENT_ID: "clientId",
  CLIENT_NAME: "clientName",
  CLIENT_GENDER: "clientGender",
  CLIENT_PHONE: "clientPhone",
  APPOINTMENT_DATE: "appointmentDate",
};

const CreateAppointment = ({
  isInViewMode = false,
}: {
  isInViewMode?: boolean;
}) => {
  useSetActiveTab(TAB_NAMES.APPOINTMENT);
  const navigate = useNavigate();
  const { appointmentId: id } = useParams();

  const appointmentId = useMemo(() => Number(id), [id]);

  const isInEditMode = useMemo(() => !!appointmentId, [appointmentId]);

  const { errorNotification, successNotification } = useNotification();

  const { isLoading: isGetAppointmentLoading, data } =
    useGetAppointmentData(appointmentId);

  const appointmentData = useMemo(() => data?.data ?? {}, [data?.data]);

  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText);

  const {
    isLoading: isUpdateLoading,
    execute: updateClient,
    isSuccess: isUpdateSuccess,
    error: isUpdateError,
  } = useUpdateAppointment(appointmentId);

  const { isLoading: isClientsLoading, data: clientList } = useGetClients({
    length: 10,
    page: 1,
    search: debouncedSearch,
  });

  const { data: serviceData } = useGetServices({
    length: 10,
    page: 1,
    search: "",
  });

  const { data: packageData } = useGetPackages({
    length: 10,
    page: 1,
    search: "",
  });

  const { data: courseData } = useGetCourses({
    length: 10,
    page: 1,
    search: "",
  });

  const { data: staffData } = useGetStaff({
    length: 10,
    page: 1,
    search: "",
  });

  const getFormattedDropdownOptions = ({
    id,
    name,
    status,
  }: {
    id: number;
    name: string;
    status: boolean;
  }) => ({
    label: name,
    value: id,
    isDisabled: !status,
  });

  const mappedServiceData = useMemo(
    () => serviceData?.data?.rows?.map(getFormattedDropdownOptions) ?? [],
    [serviceData?.data?.rows]
  );

  const mappedPackageData = useMemo(
    () => packageData?.data?.rows?.map(getFormattedDropdownOptions) ?? [],
    [packageData?.data?.rows]
  );

  const mappedCourseData = useMemo(
    () => courseData?.data?.rows?.map(getFormattedDropdownOptions) ?? [],
    [courseData?.data?.rows]
  );

  const mappedStaffData = useMemo(
    () =>
      staffData?.data?.rows?.map(
        ({
          id,
          name,
          role,
        }: {
          id: number;
          name: string;
          role: { status: boolean };
        }) => ({
          label: name,
          value: id,
          isDisabled: !role?.status,
        })
      ) ?? [],
    [staffData?.data?.rows]
  );

  const { isLoading, isSuccess, execute, error } = useCreateAppointment();

  const servicePriceById = useCallback(
    (serviceId: number, service: { id: number; price: number }[]) =>
      service?.find(({ id }: { id: number }) => serviceId === id)?.price ?? 0,
    []
  );

  const priceByAppointmentType = useCallback(
    (serviceId: number, type: number) => {
      switch (type) {
        case APPOINTMENT_TYPES.SERVICE:
          return servicePriceById(serviceId, serviceData?.data?.rows) ?? 0;
        case APPOINTMENT_TYPES.PACKAGE:
          return servicePriceById(serviceId, packageData?.data?.rows) ?? 0;
        case APPOINTMENT_TYPES.COURSE:
          return servicePriceById(serviceId, courseData?.data?.rows) ?? 0;
        default:
          return 0;
      }
    },
    [
      courseData?.data?.rows,
      packageData?.data?.rows,
      serviceData?.data?.rows,
      servicePriceById,
    ]
  );

  const mappedInitialServicePrice = useCallback(
    () =>
      appointmentData?.[FORM_FIELDS.SERVICES]?.map(
        (service: { serviceId: number; type: number; quantity: number }) => ({
          ...service,
          price:
            priceByAppointmentType(service.serviceId, service.type) *
            service.quantity,
        })
      ) ?? [initialServiceValues],
    [appointmentData, priceByAppointmentType]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      [FORM_FIELDS.NAME]: appointmentData?.[FORM_FIELDS.NAME] ?? "",
      [FORM_FIELDS.SERVICES]: mappedInitialServicePrice(),
      [FORM_FIELDS.CLIENT_ID]: appointmentData?.[FORM_FIELDS.CLIENT_ID] ?? "",
      [FORM_FIELDS.CLIENT_PHONE]:
        appointmentData?.[FORM_FIELDS.CLIENT_PHONE] ?? "",
      [FORM_FIELDS.CLIENT_NAME]:
        appointmentData?.[FORM_FIELDS.CLIENT_NAME] ?? "",
      [FORM_FIELDS.CLIENT_GENDER]:
        appointmentData?.[FORM_FIELDS.CLIENT_GENDER] ?? "",
      [FORM_FIELDS.APPOINTMENT_DATE]:
        appointmentData?.[FORM_FIELDS.APPOINTMENT_DATE] ?? "",
    },
    validationSchema: object({
      [FORM_FIELDS.CLIENT_NAME]: string().required("Required"),
      [FORM_FIELDS.APPOINTMENT_DATE]: string().required("Required"),
      [FORM_FIELDS.SERVICES]: array().of(SERVICE_SCHEMA),
    }),
    onSubmit: (data) => {
      const payload = {
        appointmentDate: data.appointmentDate,
        clientId: data.clientId,
        services: data.services.map(
          ({
            type,
            serviceId,
            staffId,
            startTime,
            endTime,
            quantity,
          }: {
            type: number;
            serviceId: number;
            staffId: number;
            startTime: number;
            endTime: number;
            quantity: number;
          }) => ({
            type: type,
            serviceId: serviceId,
            staffId: staffId,
            startTime: startTime,
            endTime: endTime,
            quantity: quantity,
          })
        ),
      };
      if (isInEditMode) {
        updateClient(payload);
        return;
      }
      execute(payload);
    },
  });

  const { values, errors, handleSubmit, setFieldValue, touched } = formik;

  useEffect(() => {
    if (isSuccess || isUpdateSuccess) {
      successNotification(
        isUpdateSuccess ? "Client Updated" : "Client Created."
      );
      navigate("/store/appointments");
    }
  }, [isSuccess, isUpdateSuccess, navigate, successNotification]);

  useEffect(() => {
    if (error || isUpdateError) {
      errorNotification();
    }
  }, [error, errorNotification, isUpdateError]);

  const isInLoading = useMemo(
    () => isLoading || isGetAppointmentLoading || isUpdateLoading,
    [isGetAppointmentLoading, isLoading, isUpdateLoading]
  );

  const isFieldDisabled = useMemo(
    () => isInViewMode || isInLoading,
    [isInLoading, isInViewMode]
  );

  const getFieldError = useCallback(
    (fieldName: string) => {
      if (
        formik.submitCount > 0 &&
        [FORM_FIELDS.CLIENT_NAME, FORM_FIELDS.CLIENT_GENDER].includes(fieldName)
      ) {
        return errors[fieldName] as string;
      }
      return touched[fieldName] ? (errors[fieldName] as string) : undefined;
    },
    [errors, formik.submitCount, touched]
  );

  const getAppointmentFieldError = (fieldName: string, index: number) => {
    return (touched[FORM_FIELDS.SERVICES] as any)?.[index]?.[fieldName]
      ? (errors[FORM_FIELDS.SERVICES] as any)?.[index]?.[fieldName]
      : undefined;
  };

  const calculateServicePrice = useCallback(
    (serviceId: number, quantity: number, type: number) => {
      if (Number.isNaN(quantity)) return 0;

      let arr = [];
      switch (type) {
        case 2:
          arr = packageData?.data.rows;
          break;
        case 3:
          arr = courseData?.data.rows;
          break;
        default:
          arr = serviceData?.data.rows;
          break;
      }

      const { price } = arr?.find(
        ({ id }: { id: number }) => serviceId === id
      ) ?? { price: 0 };

      return price * quantity;
    },
    [courseData, packageData, serviceData]
  );

  const mappedClientList = useMemo(() => {
    return clientList?.data?.rows?.map((client: any) => ({
      label: `${client.phone} (${client.name})`,
      value: client.id,
    }));
  }, [clientList?.data?.rows]);

  return (
    <div className="flex flex-col gap-[20px] h-full">
      <TabHeader label="Add Appointment" showBreadcrumb />
      <FormikProvider value={formik}>
        <div className="flex flex-col justify-between gap-6 bg-white rounded-md py-[30px] px-[42px] h-fit">
          <div className="grid grid-cols-2 gap-4 items-start">
            <SearchList
              inputClassName="!border-0 !border-b !rounded-none"
              label="Phone Number"
              onSearch={(val) => {
                setSearchText(val);
                return mappedClientList;
              }}
              value={formik.values[FORM_FIELDS.CLIENT_PHONE]}
              onChange={(val) => {
                const client = clientList?.data?.rows?.find(
                  (item: any) => val === item.id
                );
                formik.setFieldValue(FORM_FIELDS.CLIENT_ID, val);
                formik.setFieldValue(FORM_FIELDS.CLIENT_NAME, client?.name);
                formik.setFieldValue(FORM_FIELDS.CLIENT_GENDER, client?.gender);
                formik.setFieldValue(FORM_FIELDS.CLIENT_PHONE, client?.phone);
              }}
              icon={<CallIcon />}
              disabled={isFieldDisabled}
              isLoading={isClientsLoading}
            />

            <Button
              text={"Add new client"}
              primary
              icon={(color) => <AddIcon color={color} />}
              onClick={() => navigate("/store/clients/create")}
            />
          </div>
          <ErrorMessage text={getFieldError(FORM_FIELDS.NAME)} />
          <HorizontalSeparator className="!bg-[#D1D1D1]" />
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <Input
                label="Name"
                placeholder=""
                name={FORM_FIELDS.CLIENT_NAME}
                value={values[FORM_FIELDS.CLIENT_NAME] as string}
                error={getFieldError(FORM_FIELDS.CLIENT_NAME)}
                onChange={(e) =>
                  setFieldValue(FORM_FIELDS.CLIENT_NAME, e.target.value)
                }
                required
                disabled={true}
              />
              <Dropdown
                className="min-w-[120px]"
                label="Gender"
                placeholder=""
                value={values[FORM_FIELDS.CLIENT_GENDER] as string}
                error={getFieldError(FORM_FIELDS.CLIENT_GENDER)}
                options={GENDERS}
                onChange={(value) =>
                  setFieldValue(FORM_FIELDS.CLIENT_GENDER, value)
                }
                required
                disabled={true}
              />
            </div>
            <DatePicker
              label="Appointment Date"
              value={values[FORM_FIELDS.APPOINTMENT_DATE] ?? null}
              error={getFieldError(FORM_FIELDS.APPOINTMENT_DATE)}
              onChange={(value) =>
                setFieldValue(
                  FORM_FIELDS.APPOINTMENT_DATE,
                  moment(value?.toString()).format("YYYY-MM-DD")
                )
              }
              required
            />
          </div>
          <div className="bg-[#F8F7F7] rounded-lg p-4 overflow-x-scroll scrollbar-thin">
            <table className="border-none bg-transparent">
              <tbody>
                <FieldArray name={FORM_FIELDS.SERVICES}>
                  {({ insert, remove, push }) => {
                    return (
                      <>
                        {values[FORM_FIELDS.SERVICES].map(
                          (item: any, index: number, arr: any) => {
                            const hasMultipleRows = arr.length > 1;
                            return (
                              <tr
                                className="border-none align-top flex items-start justify-between h-full"
                                key={item.id}
                              >
                                <td className="w-full">
                                  {APPOINTMENT_TYPES.SERVICE === item.type && (
                                    <Dropdown
                                      label="Service"
                                      placeholder="Select Service"
                                      className={"w-full bg-white"}
                                      options={mappedServiceData}
                                      value={
                                        values[FORM_FIELDS.SERVICES]?.[index]?.[
                                          "serviceId"
                                        ] as string
                                      }
                                      onChange={(val) =>
                                        setFieldValue(
                                          `${FORM_FIELDS.SERVICES}.${index}.serviceId`,
                                          val
                                        )
                                      }
                                      disabled={isFieldDisabled}
                                      error={getAppointmentFieldError(
                                        "serviceId",
                                        index
                                      )}
                                      required
                                    />
                                  )}
                                  {APPOINTMENT_TYPES.PACKAGE === item.type && (
                                    <Dropdown
                                      label="Package"
                                      placeholder="Select Package"
                                      className={"w-full bg-white"}
                                      options={mappedPackageData}
                                      value={
                                        values[FORM_FIELDS.SERVICES]?.[index]?.[
                                          "serviceId"
                                        ] as string
                                      }
                                      onChange={(val) =>
                                        setFieldValue(
                                          `${FORM_FIELDS.SERVICES}.${index}.serviceId`,
                                          val
                                        )
                                      }
                                      disabled={isFieldDisabled}
                                      error={getAppointmentFieldError(
                                        "serviceId",
                                        index
                                      )}
                                      required
                                    />
                                  )}
                                  {APPOINTMENT_TYPES.COURSE === item.type && (
                                    <Dropdown
                                      label="Course"
                                      placeholder="Select Course"
                                      className={"w-full bg-white"}
                                      options={mappedCourseData}
                                      value={
                                        values[FORM_FIELDS.SERVICES]?.[index]?.[
                                          "serviceId"
                                        ] as string
                                      }
                                      onChange={(val) =>
                                        setFieldValue(
                                          `${FORM_FIELDS.SERVICES}.${index}.serviceId`,
                                          val
                                        )
                                      }
                                      disabled={isFieldDisabled}
                                      error={getAppointmentFieldError(
                                        "serviceId",
                                        index
                                      )}
                                      required
                                    />
                                  )}
                                </td>
                                <td className="w-[80%]">
                                  <Dropdown
                                    label="Staff Assigned"
                                    placeholder="Select Staff"
                                    className={"w-full bg-white"}
                                    options={mappedStaffData}
                                    value={
                                      values[FORM_FIELDS.SERVICES]?.[index]?.[
                                        "staffId"
                                      ] as string
                                    }
                                    onChange={(val) =>
                                      setFieldValue(
                                        `${FORM_FIELDS.SERVICES}.${index}.staffId`,
                                        val
                                      )
                                    }
                                    disabled={isFieldDisabled}
                                    error={getAppointmentFieldError(
                                      "staffId",
                                      index
                                    )}
                                    required
                                  />
                                </td>
                                <td className="w-[120px]">
                                  <TimePicker
                                    label="Start Time"
                                    className={"w-full bg-white"}
                                    value={
                                      values[FORM_FIELDS.SERVICES]?.[index]?.[
                                        "startTime"
                                      ] as string
                                    }
                                    onChange={({ target: { value } }) =>
                                      setFieldValue(
                                        `${FORM_FIELDS.SERVICES}.${index}.startTime`,
                                        value
                                      )
                                    }
                                    disabled={isFieldDisabled}
                                    error={getAppointmentFieldError(
                                      "startTime",
                                      index
                                    )}
                                    required
                                  />
                                </td>
                                <td className="w-[120px]">
                                  <TimePicker
                                    label="End Time"
                                    className={"w-full bg-white"}
                                    value={
                                      values[FORM_FIELDS.SERVICES]?.[index]?.[
                                        "endTime"
                                      ] as string
                                    }
                                    onChange={({ target: { value } }) =>
                                      setFieldValue(
                                        `${FORM_FIELDS.SERVICES}.${index}.endTime`,
                                        value
                                      )
                                    }
                                    disabled={isFieldDisabled}
                                    error={getAppointmentFieldError(
                                      "endTime",
                                      index
                                    )}
                                    required
                                  />
                                </td>
                                <td className="w-[80%]">
                                  <Input
                                    type="number"
                                    name="quantity"
                                    label="Quantity"
                                    placeholder="Quantity"
                                    className={"w-full [&>div>div]:bg-white"}
                                    value={
                                      values[FORM_FIELDS.SERVICES]?.[index]?.[
                                        "quantity"
                                      ] as string
                                    }
                                    onChange={({ target: { value } }) => {
                                      setFieldValue(
                                        `${FORM_FIELDS.SERVICES}.${index}.quantity`,
                                        parseInt(value ?? 0)
                                      );

                                      setFieldValue(
                                        `${FORM_FIELDS.SERVICES}.${index}.price`,
                                        calculateServicePrice(
                                          values[FORM_FIELDS.SERVICES]?.[index]
                                            .serviceId,
                                          Number(value),
                                          values[FORM_FIELDS.SERVICES]?.[index]
                                            .type
                                        ).toString()
                                      );
                                    }}
                                    disabled={isFieldDisabled}
                                    error={getAppointmentFieldError(
                                      "quantity",
                                      index
                                    )}
                                    required
                                  />
                                </td>
                                <td className="w-[80%]">
                                  <Input
                                    name="price"
                                    label="Price (₹)"
                                    placeholder="Price"
                                    className={"w-full [&>div>div]:bg-white"}
                                    value={
                                      values[FORM_FIELDS.SERVICES]?.[index]?.[
                                        "price"
                                      ] as string
                                    }
                                    onChange={({ target: { value } }) =>
                                      setFieldValue(
                                        `${FORM_FIELDS.SERVICES}.${index}.price`,
                                        parseInt(value ?? 0)
                                      )
                                    }
                                    error={getAppointmentFieldError(
                                      "price",
                                      index
                                    )}
                                    disabled={true}
                                    required
                                  />
                                </td>
                                <td className="flex items-end h-full">
                                  {!isFieldDisabled && hasMultipleRows && (
                                    <div
                                      className="flex h-full justify-center items-center top-4 mt-[38px] cursor-pointer"
                                      onClick={() => remove(index)}
                                    >
                                      <CloseIcon color="#B22234" />
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          }
                        )}

                        {!isInViewMode && (
                          <div className="flex items-center gap-3">
                            <Button
                              text={"Service"}
                              className="mt-4 ml-2"
                              icon={(color) => <AddIcon color={"#0c8ce9"} />}
                              onClick={() => push(initialServiceValues)}
                              primary
                              ghost
                            />
                            <Button
                              text={"Package"}
                              className="mt-4 ml-2"
                              icon={(color) => <AddIcon color={"#0c8ce9"} />}
                              onClick={() =>
                                push({
                                  ...initialServiceValues,
                                  type: 2,
                                  id: generateRandomId(),
                                })
                              }
                              primary
                              ghost
                            />
                            <Button
                              text={"Course"}
                              className="mt-4 ml-2"
                              icon={(color) => <AddIcon color={"#0c8ce9"} />}
                              onClick={() =>
                                push({
                                  ...initialServiceValues,
                                  type: 3,
                                  id: generateRandomId(),
                                })
                              }
                              primary
                              ghost
                            />
                          </div>
                        )}
                      </>
                    );
                  }}
                </FieldArray>
              </tbody>
            </table>
          </div>
          <Button
            text={isLoading ? "Loading..." : "Submit"}
            onClick={handleSubmit}
            primary
            className="h-[40px] px-10"
            isDisabled={isFieldDisabled}
          />
        </div>
      </FormikProvider>
    </div>
  );
};

export default CreateAppointment;
